// frontend/src/App.jsx
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AdminProvider } from './contexts/AdminContext';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import SEO from './components/SEO';
import Home from './pages/Home';
const SafetyRegulations = React.lazy(() => import('./pages/SafetyRegulations'));
const SearchResults = React.lazy(() => import('./pages/SearchResults'));
const SafetyMaterialsHub = React.lazy(() => import('./pages/SafetyMaterialsHub'));
const SafetyEquipmentGuide = React.lazy(() => import('./pages/SafetyEquipmentGuide'));
const SafetyMaterialItemsGuide = React.lazy(() => import('./pages/SafetyMaterialItemsGuide'));
const BoardList = React.lazy(() => import('./pages/BoardList'));
const BoardDetail = React.lazy(() => import('./pages/BoardDetail'));
const BoardWrite = React.lazy(() => import('./pages/BoardWrite'));
const FieldQnAList = React.lazy(() => import('./pages/FieldQnAList'));
const FieldQnADetail = React.lazy(() => import('./pages/FieldQnADetail'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const Contact = React.lazy(() => import('./pages/Contact'));
const SafetyFormsPage = React.lazy(() => import('./pages/SafetyFormsPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const PasswordRecovery = React.lazy(() => import('./pages/PasswordRecovery'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const Account = React.lazy(() => import('./pages/Account'));

const PageFallback = ({ label = '페이지' }) => (
  <div className="flex min-h-[420px] items-center justify-center" role="status" aria-live="polite">
    <div className="text-center">
      <div className="spinner mx-auto mb-3" />
      <p className="text-sm text-gray-600">{label}을 준비하고 있습니다…</p>
    </div>
  </div>
);

const DeferredPage = ({ children, label }) => (
  <Suspense fallback={<PageFallback label={label} />}>
    {children}
  </Suspense>
);

const NoIndexPage = ({ title, children }) => {
  const location = useLocation();
  return (
    <>
      <SEO title={`${title} | 안전사관학교`} url={location.pathname} noIndex />
      {children}
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AdminProvider>
        <Router>
        <Routes>
          {/* 홈 검색 히어로는 Home 컴포넌트에서 하나만 표시 */}
          <Route path="/" element={
            <MainLayout>
              <Home />
            </MainLayout>
          } />
          
          {/* 안전법규 페이지 */}
          <Route path="/safety-regulations/*" element={
            <DeferredPage label="안전법규">
              <SafetyRegulations />
            </DeferredPage>
          } />
          
          {/* 검색 결과 페이지: 사이드바 없이 전체 너비(모바일에서 콘텐츠 폭 확보) */}
          <Route path="/search" element={
            <MainLayout hideSidebar={true}>
              <NoIndexPage title="검색 결과"><DeferredPage label="검색 결과"><SearchResults /></DeferredPage></NoIndexPage>
            </MainLayout>
          } />
          
          {/* 안전자재 */}
          <Route path="/safety-materials" element={
            <MainLayout>
              <DeferredPage label="안전자재"><SafetyMaterialsHub /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/safety-materials/ppe" element={
            <MainLayout>
              <DeferredPage label="보호구 가이드"><SafetyEquipmentGuide /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/safety-materials/:categoryId" element={
            <MainLayout>
              <DeferredPage label="안전자재 가이드"><SafetyMaterialItemsGuide /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/safety-equipment" element={<Navigate to="/safety-materials/ppe" replace />} />
          <Route path="/safety-equipment/*" element={<Navigate to="/safety-materials/ppe" replace />} />

          {/* 자유게시판 */}
          <Route path="/board/write" element={
            <MainLayout hideSidebar={true}>
              <NoIndexPage title="게시글 작성"><DeferredPage label="게시글 작성"><ProtectedRoute><BoardWrite /></ProtectedRoute></DeferredPage></NoIndexPage>
            </MainLayout>
          } />
          <Route path="/board/:id" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="게시글"><BoardDetail /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/board" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="자유게시판"><BoardList /></DeferredPage>
            </MainLayout>
          } />

          <Route path="/field-qna/:id" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="현장 질문"><FieldQnADetail /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/field-qna" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="현장 Q&A"><FieldQnAList /></DeferredPage>
            </MainLayout>
          } />

          {/* 안전 서식 PDF 서명 */}
          <Route path="/safety-forms" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="안전 서식 도구">
                <SafetyFormsPage />
              </DeferredPage>
            </MainLayout>
          } />
          
          {/* 운영자 관리 페이지 */}
          <Route path="/admin" element={
            <MainLayout hideSidebar={true}>
              <NoIndexPage title="운영자 페이지"><DeferredPage label="운영자 페이지"><AdminPage /></DeferredPage></NoIndexPage>
            </MainLayout>
          } />

          {/* 정책/문의 페이지 */}
          <Route path="/privacy-policy" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="개인정보처리방침"><PrivacyPolicy /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/terms" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="이용약관"><TermsOfService /></DeferredPage>
            </MainLayout>
          } />
          <Route path="/contact" element={
            <MainLayout hideSidebar={true}>
              <DeferredPage label="문의 페이지"><Contact /></DeferredPage>
            </MainLayout>
          } />

          <Route path="/login" element={<MainLayout hideSidebar={true}><NoIndexPage title="로그인"><DeferredPage label="로그인"><Login /></DeferredPage></NoIndexPage></MainLayout>} />
          <Route path="/register" element={<MainLayout hideSidebar={true}><NoIndexPage title="회원가입"><DeferredPage label="회원가입"><Register /></DeferredPage></NoIndexPage></MainLayout>} />
          <Route path="/forgot-password" element={<MainLayout hideSidebar={true}><NoIndexPage title="비밀번호 찾기"><DeferredPage label="비밀번호 찾기"><PasswordRecovery mode="request" /></DeferredPage></NoIndexPage></MainLayout>} />
          <Route path="/reset-password" element={<MainLayout hideSidebar={true}><NoIndexPage title="비밀번호 재설정"><DeferredPage label="비밀번호 재설정"><PasswordRecovery mode="reset" /></DeferredPage></NoIndexPage></MainLayout>} />
          <Route path="/verify-email" element={<MainLayout hideSidebar={true}><NoIndexPage title="이메일 인증"><DeferredPage label="이메일 인증"><VerifyEmail /></DeferredPage></NoIndexPage></MainLayout>} />
          <Route path="/account" element={<MainLayout hideSidebar={true}><NoIndexPage title="내 계정"><DeferredPage label="내 계정"><ProtectedRoute><Account /></ProtectedRoute></DeferredPage></NoIndexPage></MainLayout>} />
          
          {/* 404 페이지 - 마지막에 위치해야 함 */}
          <Route path="*" element={
            <MainLayout>
              <>
                <NoIndexPage title="페이지를 찾을 수 없습니다" />
                <div className="flex flex-col items-center justify-center py-16">
                  <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-8">페이지를 찾을 수 없습니다</p>
                  <a
                    href="/"
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    홈으로 돌아가기
                  </a>
                </div>
              </>
            </MainLayout>
          } />
        </Routes>
        </Router>
      </AdminProvider>
    </AuthProvider>
  );
};

export default App;
